import { createFileRoute, notFound, Link, useRouter } from "@tanstack/react-router";
import { findArticle, type HelpArticle } from "@/lib/help-content";
import {
  HelpArticleLayout,
  HelpNav,
  HelpFooter,
  NAVY,
  PAPER,
  GOLD,
  INK,
  INK_2,
  NAVY_TEXT,
  FONT_SERIF,
  FONT_SANS,
  FONT_MONO,
} from "@/components/marketing/help-article-layout";

export const Route = createFileRoute("/help/$slug")({
  loader: ({ params }) => {
    const article = findArticle(params.slug);
    if (!article) throw notFound();
    return { article };
  },
  head: ({ params, loaderData }) => {
    const article = loaderData?.article;
    const title = article ? `${article.title}: CapitalDart Help` : "Help: CapitalDart";
    const description = article ? article.summary : "CapitalDart help center.";
    const url = `https://capitaldart.com/help/${params.slug}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: ArticlePage,
  errorComponent: ArticleError,
  notFoundComponent: ArticleNotFound,
});

function ArticlePage() {
  const { article } = Route.useLoaderData() as { article: HelpArticle };
  return <HelpArticleLayout article={article} />;
}

function ArticleError({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();
  return (
    <main
      style={{ background: PAPER, color: INK, fontFamily: FONT_SANS }}
      className="min-h-screen antialiased"
    >
      <HelpNav />
      <section style={{ padding: "96px 0" }}>
        <div className="mx-auto" style={{ maxWidth: 680, padding: "0 32px", textAlign: "center" }}>
          <h1
            style={{
              fontFamily: FONT_SERIF,
              fontSize: 36,
              fontWeight: 500,
              margin: "0 0 16px",
            }}
          >
            Something went wrong.
          </h1>
          <p style={{ color: INK_2, fontSize: 16, lineHeight: 1.6 }}>{error.message}</p>
          <div
            style={{
              marginTop: 24,
              display: "flex",
              justifyContent: "center",
              gap: 20,
            }}
          >
            <button
              type="button"
              onClick={() => {
                void router.invalidate();
                reset();
              }}
              style={{
                background: NAVY,
                color: NAVY_TEXT,
                padding: "10px 18px",
                borderRadius: 6,
                fontFamily: FONT_SANS,
                fontSize: 14,
                fontWeight: 600,
                border: "none",
                cursor: "pointer",
              }}
            >
              Try again
            </button>
            <Link
              to="/help"
              style={{
                color: INK,
                textDecoration: "none",
                borderBottom: `2px solid ${GOLD}`,
                paddingBottom: 3,
                fontFamily: FONT_SANS,
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              Back to help center
            </Link>
          </div>
        </div>
      </section>
      <HelpFooter />
    </main>
  );
}

function ArticleNotFound() {
  const { slug } = Route.useParams();
  return (
    <main
      style={{ background: PAPER, color: INK, fontFamily: FONT_SANS }}
      className="min-h-screen antialiased"
    >
      <HelpNav />
      <section style={{ padding: "96px 0" }}>
        <div className="mx-auto" style={{ maxWidth: 680, padding: "0 32px", textAlign: "center" }}>
          <div
            style={{
              fontFamily: FONT_MONO,
              fontSize: 12,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: GOLD,
              marginBottom: 14,
            }}
          >
            Article not found
          </div>
          <h1
            style={{
              fontFamily: FONT_SERIF,
              fontSize: 36,
              fontWeight: 500,
              margin: "0 0 12px",
            }}
          >
            We don't have an article at /help/{slug}.
          </h1>
          <p style={{ color: INK_2, fontSize: 16, lineHeight: 1.6 }}>
            It may have moved, or the link may be wrong. Head back to the help center and search for
            what you need.
          </p>
          <div style={{ marginTop: 24 }}>
            <Link
              to="/help"
              style={{
                color: INK,
                textDecoration: "none",
                borderBottom: `2px solid ${GOLD}`,
                paddingBottom: 3,
                fontFamily: FONT_SANS,
                fontSize: 15,
                fontWeight: 600,
              }}
            >
              Back to help center
            </Link>
          </div>
        </div>
      </section>
      <HelpFooter />
    </main>
  );
}
